import { useState, MouseEvent, useMemo, useCallback } from 'react';

import { Close, SocialDistance } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  FilterOptionsState,
  IconButton,
  InputAdornment,
  Popper,
  ClickAwayListener,
  InputBase,
  Box,
  ButtonProps,
  CircularProgress,
} from '@mui/material';
import { useTheme, styled } from '@mui/material/styles';
import { matchSorter } from 'match-sorter';

import { OrganizationAutocomplete } from 'components/OrganizationAutocomplete';
import { useCandidateAdminContext, useUserContext } from 'hooks';
import { OrganizationProps } from 'types';

const StyledPopper = styled(Popper)(({ theme }) => ({
  border: `1px solid ${theme.palette.mode === 'light' ? '#e1e4e8' : '#30363d'}`,
  boxShadow: `0 8px 24px ${
    theme.palette.mode === 'light' ? 'rgba(149, 157, 165, 0.2)' : 'rgb(1, 4, 9)'
  }`,
  borderRadius: 6,
  width: 310,
  zIndex: theme.zIndex.modal,
  fontSize: 13,
  color: theme.palette.mode === 'light' ? '#24292e' : '#c9d1d9',
  backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1c2128',
}));

const StyledInput = styled(InputBase)(({ theme }) => ({
  padding: 10,
  width: '100%',
  borderBottom: `1px solid ${
    theme.palette.mode === 'light' ? '#eaecef' : '#30363d'
  }`,
  '& input': {
    borderRadius: 4,
    backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#0d1117',
    padding: 8,
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    border: `1px solid ${
      theme.palette.mode === 'light' ? '#eaecef' : '#30363d'
    }`,
    fontSize: 14,
    '&:focus': {
      boxShadow: `0px 0px 0px 3px ${
        theme.palette.mode === 'light'
          ? 'rgba(3, 102, 214, 0.3)'
          : 'rgb(12, 45, 107)'
      }`,
      borderColor: theme.palette.mode === 'light' ? '#0366d6' : '#388bfd',
    },
  },
}));

export const Masquarade = (props: ButtonProps) => {
  const { startMasquerade } = useUserContext();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [organization, setOrganization] = useState<OrganizationProps | null>(
    null,
  );
  const [startingMasquarade, setStartingMasquarade] = useState(false);
  const theme = useTheme();
  const { organizationArray } = useCandidateAdminContext();
  const organizationOptions = useMemo(
    () => organizationArray.filter((org) => !!org.name || !org.parent_id),
    [organizationArray],
  );
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    if (anchorEl) {
      anchorEl.focus();
    }
    setAnchorEl(null);
  };
  const handleMasquarade = useCallback(async () => {
    if (organization) {
      setStartingMasquarade(true);
      await startMasquerade(organization.id);
      setStartingMasquarade(false);
      window.location.href = './';
    }
  }, [organization, startMasquerade]);

  const open = Boolean(anchorEl);
  const id = open ? 'organization-label' : undefined;

  return (
    <>
      <IconButton
        disableRipple
        aria-label='masquarade'
        aria-describedby={id}
        onClick={handleClick}
        {...props}
      >
        {startingMasquarade ? <CircularProgress /> : <SocialDistance />}
      </IconButton>
      <StyledPopper
        id={id}
        open={open}
        anchorEl={anchorEl}
        placement='bottom-start'
      >
        <ClickAwayListener onClickAway={handleClose}>
          <div>
            <Box
              sx={{
                borderBottom: `1px solid ${
                  theme.palette.mode === 'light' ? '#eaecef' : '#30363d'
                }`,
                padding: '8px 10px',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              Pick employer to act as{' '}
              {organization && (
                <LoadingButton
                  onClick={handleMasquarade}
                  loading={startingMasquarade}
                  sx={{ ml: 'auto' }}
                  size='small'
                  variant='contained'
                  color='success'
                >
                  Confirm
                </LoadingButton>
              )}
              {organizationOptions.length === 0 && (
                <CircularProgress size={5} />
              )}
            </Box>
            <OrganizationAutocomplete
              value={organization}
              characterBreak={30}
              options={organizationOptions}
              getOptionLabel={(org: OrganizationProps) => org.name || ''}
              disabled={organizationArray.length === 0}
              isOptionEqualToValue={(
                opt: OrganizationProps,
                val: OrganizationProps,
              ) => opt.id === val.id}
              renderOption={(props, option) =>
                [props, option] as React.ReactNode
              }
              filterOptions={(
                options: Array<OrganizationProps>,
                { inputValue }: FilterOptionsState<OrganizationProps>,
              ) => matchSorter(options, inputValue, { keys: ['name'] })}
              fullWidth
              size='small'
              filterSelectedOptions
              renderInput={(params) => (
                <StyledInput
                  ref={params.InputProps.ref}
                  inputProps={params.inputProps}
                  autoFocus
                  placeholder='Filter labels'
                  endAdornment={
                    organization && (
                      <InputAdornment position='end'>
                        <IconButton onClick={() => setOrganization(null)}>
                          <Close />
                        </IconButton>
                      </InputAdornment>
                    )
                  }
                />
              )}
              onChange={(_, employer: OrganizationProps) => {
                setOrganization(employer);
              }}
            />
          </div>
        </ClickAwayListener>
      </StyledPopper>
    </>
  );
};
